import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api, formatApiErrorDetail } from '@/lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ADMIN } from '@/constants/testIds';

function useAdminAuth() {
  const [ok, setOk] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/auth/me')
      .then(() => { if (alive) setOk(true); })
      .catch(() => { if (alive) setOk(false); });
    return () => { alive = false; };
  }, []);
  return ok;
}

export default function AdminDashboard() {
  const ok = useAdminAuth();
  const navigate = useNavigate();
  if (ok === null) return <div className="min-h-screen flex items-center justify-center text-vl-ink/55">Loading…</div>;
  if (ok === false) return <Navigate to="/admin/login" replace />;

  async function logout() {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    navigate('/admin/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-vl-bg2">
      <header className="border-b hairline bg-vl-bg">
        <div className="vl-container h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-vl-ink text-vl-bg font-serif-display">V</span>
            <span className="font-serif-display text-lg text-vl-ink">Valanti Labs · Admin</span>
          </div>
          <button
            onClick={logout}
            data-testid={ADMIN.logout}
            className="text-sm text-vl-ink/70 hover:text-vl-ink"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="vl-container py-10">
        <Tabs defaultValue="early">
          <TabsList className="bg-vl-surface border hairline rounded-full p-1">
            <TabsTrigger data-testid={ADMIN.tabSubmissions} value="early" className="rounded-full">Early Access</TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabContact} value="contact" className="rounded-full">Contact</TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabFaqs} value="faqs" className="rounded-full">FAQs</TabsTrigger>
            <TabsTrigger data-testid={ADMIN.tabResearch} value="research" className="rounded-full">Research</TabsTrigger>
          </TabsList>

          <TabsContent value="early"><SubmissionsTab endpoint="/admin/submissions/early-access" kind="early" /></TabsContent>
          <TabsContent value="contact"><SubmissionsTab endpoint="/admin/submissions/contact" kind="contact" /></TabsContent>
          <TabsContent value="faqs"><FAQsTab /></TabsContent>
          <TabsContent value="research"><ResearchTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function SubmissionsTab({ endpoint, kind }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(endpoint).then((r) => setRows(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load'));
  }, [endpoint]);

  return (
    <div className="mt-6 rounded-3xl bg-vl-surface border hairline overflow-hidden">
      {err && <p className="p-6 text-red-700">{err}</p>}
      {rows === null && !err && <p className="p-6 text-vl-ink/60">Loading…</p>}
      {rows && rows.length === 0 && <p className="p-6 text-vl-ink/60">No submissions yet.</p>}
      {rows && rows.length > 0 && (
        <div className="divide-y hairline">
          {rows.map((r) => (
            <div key={r.id} className="p-6 grid grid-cols-1 md:grid-cols-12 gap-4 text-sm">
              <div className="md:col-span-3">
                <p className="font-medium text-vl-ink">
                  {kind === 'early' ? `${r.first_name} ${r.last_name}` : r.name}
                </p>
                <p className="text-vl-ink/65">{r.email}</p>
                <p className="text-xs font-mono text-vl-ink/50 mt-1">{new Date(r.created_at).toLocaleString()}</p>
              </div>
              <div className="md:col-span-3 text-vl-ink/75">
                {kind === 'early' ? (
                  <>
                    <p><span className="text-vl-ink/50">Country:</span> {r.country}</p>
                    <p><span className="text-vl-ink/50">Interest:</span> {r.primary_interest}</p>
                  </>
                ) : (
                  <>
                    <p><span className="text-vl-ink/50">Topic:</span> {r.topic}</p>
                    <p><span className="text-vl-ink/50">Org:</span> {r.organisation || '—'}</p>
                  </>
                )}
              </div>
              <div className="md:col-span-6 text-vl-ink/75 whitespace-pre-wrap">{r.message || '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_FAQ = { question: '', answer: '', category: 'general', order: 0 };

function FAQsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FAQ);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState('');

  async function load() {
    try { setRows((await api.get('/faqs')).data); } catch { setErr('Failed to load FAQs'); }
  }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    setErr('');
    try {
      if (editing) {
        await api.put(`/admin/faqs/${editing}`, form);
      } else {
        await api.post('/admin/faqs', form);
      }
      setForm(EMPTY_FAQ);
      setEditing(null);
      load();
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || 'Save failed'); }
  }

  async function del(id) {
    if (!window.confirm('Delete this FAQ?')) return;
    try { await api.delete(`/admin/faqs/${id}`); load(); } catch { /* ignore */ }
  }

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form onSubmit={save} className="rounded-3xl bg-vl-surface border hairline p-6 space-y-4">
        <h3 className="font-serif-display text-xl text-vl-ink">{editing ? 'Edit FAQ' : 'New FAQ'}</h3>
        <input className={admInput} placeholder="Question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} required />
        <textarea className={admInput + ' min-h-[140px]'} placeholder="Answer" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} required />
        <div className="grid grid-cols-2 gap-3">
          <input className={admInput} placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <input type="number" className={admInput} placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} />
        </div>
        {err && <p className="text-sm text-red-700">{err}</p>}
        <div className="flex gap-3">
          <button className="rounded-full bg-vl-ink text-vl-bg px-5 py-2.5 text-sm">{editing ? 'Update' : 'Create'}</button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_FAQ); }} className="rounded-full border border-vl-ink/20 px-5 py-2.5 text-sm">
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="rounded-3xl bg-vl-surface border hairline divide-y hairline">
        {rows.map((r) => (
          <div key={r.id} className="p-5">
            <p className="font-medium text-vl-ink">{r.question}</p>
            <p className="text-sm text-vl-ink/70 mt-1 line-clamp-2">{r.answer}</p>
            <p className="text-xs font-mono text-vl-ink/45 mt-2">{r.category} · order {r.order}</p>
            <div className="mt-3 flex gap-3">
              <button onClick={() => { setEditing(r.id); setForm({ question: r.question, answer: r.answer, category: r.category, order: r.order }); }} className="text-sm text-vl-ink underline">Edit</button>
              <button onClick={() => del(r.id)} className="text-sm text-red-700 underline">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_RES = { title: '', summary: '', body: '', period: 'Current stage', published: true };

function ResearchTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_RES);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState('');

  async function load() {
    try { setRows((await api.get('/admin/research')).data); } catch { setErr('Failed to load posts'); }
  }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    setErr('');
    try {
      if (editing) await api.put(`/admin/research/${editing}`, form);
      else await api.post('/admin/research', form);
      setForm(EMPTY_RES); setEditing(null); load();
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || 'Save failed'); }
  }

  async function del(id) {
    if (!window.confirm('Delete this research post?')) return;
    try { await api.delete(`/admin/research/${id}`); load(); } catch { /* ignore */ }
  }

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form onSubmit={save} className="rounded-3xl bg-vl-surface border hairline p-6 space-y-4">
        <h3 className="font-serif-display text-xl text-vl-ink">{editing ? 'Edit research post' : 'New research post'}</h3>
        <input className={admInput} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        <input className={admInput} placeholder="Period (e.g. 2026)" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
        <textarea className={admInput + ' min-h-[80px]'} placeholder="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} required />
        <textarea className={admInput + ' min-h-[200px]'} placeholder="Body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
        </label>
        {err && <p className="text-sm text-red-700">{err}</p>}
        <div className="flex gap-3">
          <button className="rounded-full bg-vl-ink text-vl-bg px-5 py-2.5 text-sm">{editing ? 'Update' : 'Create'}</button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_RES); }} className="rounded-full border border-vl-ink/20 px-5 py-2.5 text-sm">
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="rounded-3xl bg-vl-surface border hairline divide-y hairline max-h-[80vh] overflow-y-auto">
        {rows.map((r) => (
          <div key={r.id} className="p-5">
            <p className="mono-label text-vl-violet">{r.period} · {r.published ? 'published' : 'draft'}</p>
            <p className="font-serif-display text-lg text-vl-ink mt-1">{r.title}</p>
            <p className="text-sm text-vl-ink/70 mt-1 line-clamp-2">{r.summary}</p>
            <div className="mt-3 flex gap-3">
              <button onClick={() => { setEditing(r.id); setForm({ title: r.title, summary: r.summary, body: r.body, period: r.period, published: r.published }); }} className="text-sm text-vl-ink underline">Edit</button>
              <button onClick={() => del(r.id)} className="text-sm text-red-700 underline">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const admInput =
  'w-full rounded-xl border border-vl-ink/15 bg-transparent px-4 py-2.5 text-sm text-vl-ink focus:outline-none focus:border-vl-violet focus:ring-1 focus:ring-vl-violet';
